import { useDispatch } from "react-redux";
import { populateAffiliation, setCompanyNumber, setForm, setHasAffiliation, setNatureOfBusiness } from "../../features/gms-proposal-forms/basic-information/basic-information.slice";
import { setAgeByLabel, setGroupClassification, setOtherSpecification, setOtherSpecificationToG, setSelectedFundingArrangementsBox, setSelectedSubTypesFromAPI, setTypeOfGroup } from "../../features/gms-proposal-forms/gc-tog-ea-mop/gc-tog-ea-mop-slice";
import { COVERAGE_TYPE_IDS, PRODUCT_TYPE_IDS, PROPOSAL_TYPE_IDS } from "../../constant/gms-constant";
import { setFAmountOfLoans, setSelectedAmountOfLoansBox, setSelectedAmountOfLoansInputFromAPI, setUpdateAmount, updateAmountOfLoansRiderFAmount } from "../../features/gms-proposal-forms/amount-of-loans/amount-loans.slice";
import { setLevelRankingFromAPI } from "../../features/gms-proposal-forms/coverage/level-ranking/coverage-type-level-ranking.slice";
import { setSelectedUniformCoverage } from "../../features/gms-proposal-forms/coverage/uniform-coverage/coverage-type-uniform-coverage.slice";
import { setSalaryRankFromAPI } from "../../features/gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.slice";
import { optionForGroupDengueRider } from "../../data/group-insurance";
import { populateRidersLevelRanking } from "../../helper/riders/populate-level-ranking-rider";
import { populateRidersUniformCoverage } from "../../helper/riders/populate-uniform-coverage-rider";
import { populateRidersSalaryRank } from "../../helper/riders/populate-salary-rank-rider";
import { populateRidersAmountOfLoans } from "../../helper/riders/populate-amount-loan-rider";
import { setSelectedRiders } from "../../features/gms-proposal-forms/attachable-rider/attachable-riders.slice";
import { setCurrentGroupName, setCurrentStatus, setProposalExpiredAt, setProposalType, updateChannelAndSubName } from "../../features/gms-proposal-forms/steps-channel-proposal_as/steps-channel-proposal_as.slice";
import { v4 as uuidv4 } from "uuid";
import { setIsPrototype } from "../../features/gms-state-and-modal/modal-slice";
import { setProposalPreparedBy, setProposalStatus, setUpdateStatus } from "../../features/gms-state-and-modal/state-slice";
import { generateRemainingAgeRate } from "../../utils/generate-remaining-age.utils";
import { useQueries } from "../services/queries/use-queries";
import { useCallback, useMemo } from "react";
import { Affiliation } from "../../features/gms-proposal-forms/basic-information/basic-information.state";
import { setExistingCsvFilePath, setSelectedBasicPlansBox, setSelectedCoverageTypeBox, setSelectedProductTypeBox, setSelectedProposalTypeBox, setSelectedPrototypeTypeBox } from "../../features/gms-proposal-forms/types-proposal-product-basic-plan/types-proposal-product-basic-plan-slice";
import { addBorrowerAgeOption, setBorrowerAgeFromAPI, setBorrowerAgeIsShow, updateBorrowerAgeValue } from "../../features/gms-proposal-forms/age-bracket-and-benefit-amount/age-bracket-and-benefit-amount.slice";
import { populateSupportingDocuments, setCommissionValue, setNotesContent, setSelectedPaymentTermBox, setServiceValue, setUpdateLoanMaturityMonths } from "../../features/gms-proposal-forms/payment_term-loan_maturity-commission_services-notes-files/payment_term-loan_maturity-commission_services-notes-files.slice";
import { ProposalData, Riders } from "../../api/gms/proposal/proposal-api-state";
import { SubTypeOption } from "../../api/gms/group-category/group-category-api-state";
import { RiderCategoryType } from "../../features/gms-proposal-forms/attachable-rider/attachable-riders.state";
import { selectApprovalData, setApprovalData } from "../../features/actuarial/approval/approval-slice";
import { setPolicyNumber } from "../../features/ebam/plicy-data-page/policy-data-page.slice";

export const usePopulateProposal = (proposal?: ProposalData) => {

    const dispatch = useDispatch();

    const { typeOfCompanyNumber, typeOfContactNumber } = useQueries();

    const companyNumberOptions = useMemo(() => {
        return (
            typeOfCompanyNumber?.map((item) => ({
                id: item.id,
                name: item.name,
            })) || []
        );
    }, [typeOfCompanyNumber]);

    const companyContactNumberOptions = useMemo(() => {
        return (
            typeOfContactNumber?.map((item) => ({
                id: item.id,
                name: item.name,
            })) || []
        );
    }, [typeOfContactNumber]);

    const populateApproval = useCallback(() => {
        if(!proposal) return;
        if(proposal.actuarial_approval) {
            dispatch(setApprovalData(proposal.actuarial_approval))
        }
    }, [dispatch, proposal]);

    // DONE
    const populateChannelType = useCallback(() => {
        if(!proposal) return;
        const formatChannel = {
            selectedChannelBox: proposal.channel_type ? { id: proposal.channel_type.id, name: proposal.channel_type.name, } : null,
            channelSubName: proposal.channel_type?.channel_name ?? null,
            channelSubNumber: String(proposal.channel_type?.channel_number) != null
                    ? String(proposal.channel_type.channel_number)
                    : null,
            channelSubEmail: proposal.channel_type?.channel_email ?? null,
        };
        
        dispatch(updateChannelAndSubName(formatChannel))
        dispatch(setProposalType(proposal.proposal_type))
    }, [dispatch, proposal]);
    // DONE
    const populateAffiliates = useCallback(() => {
        if(!proposal) return;

        const affiliateFormat: Affiliation[] = proposal.affiliates.map((aff) => ({
            id: String(aff.id),
            name: aff.company_name,
            address: aff.address,
            tinNumber: aff.tin_number,
        }));

        dispatch(populateAffiliation(affiliateFormat));
        dispatch(setHasAffiliation(affiliateFormat.length === 0 ? false : true));
    }, [dispatch, proposal]);

    const populateCommissionRateAndServiceFee = useCallback(() => {
        if(!proposal) return;
        const commissionValueFormat = Number(proposal.commission_rate)
        const serviceRateFormat = Number(proposal.service_fee)
        dispatch(setCommissionValue(commissionValueFormat))
        dispatch(setServiceValue(serviceRateFormat))
    }, [dispatch, proposal]);
    // DONE
    const populateBasicInfo = useCallback(() => {
        if (!proposal) return;
        // Company TIN / SEC type
        const companyNumberId = proposal.company_details.company_tin_type_id ?? proposal.company_details.company_sec_type_id;
        const companyNumberValue = companyNumberOptions.find((opt) => opt.id === companyNumberId) ?? null;
        dispatch(setCompanyNumber({type: "comNumber",value: companyNumberValue,}));

        // Company contact number type
        const companyContactNumberId = proposal.company_contact.company_mobile_type_id ?? proposal.company_contact.company_telephone_type_id;
        const companyContactNumberValue = companyContactNumberOptions.find((opt) => opt.id === companyContactNumberId) ?? null;
        dispatch(setCompanyNumber({type: "comContactNumber", value: companyContactNumberValue,}));

        // Contact person contact number type
        const contactPersonNumberId =
            proposal.contact_person_contact.contact_person_mobile_type_id ??
            proposal.contact_person_contact.contact_person_telephone_type_id;

        const contactPersonNumberValue =companyContactNumberOptions.find((opt) => opt.id === contactPersonNumberId) ?? null;
        dispatch(setCompanyNumber({type: "contactPersonNumber", value: contactPersonNumberValue,}));

            dispatch(setForm({
                groupName: proposal?.group_name ?? "",
                noOfLives: proposal?.number_of_lives ?? 0,
                // companyMobileNo: proposal?.fax_number?.replace("+63", "") ?? "",
                companyMobileNo: proposal.company_contact.company_mobile?.replace("+63", "") ?? "",
                companyTelephone: proposal.company_contact.company_telephone ?? "",
                completeBusinessAddress: proposal?.business_address ?? "",
                proposalAddress: proposal?.proposal_addressee ?? "",
                email: proposal?.email ?? "",
                addressDesignation: proposal?.addressee_designation ?? "",
                designation: proposal?.designation ?? "",
                contactPersonSalutation: proposal?.contact_person?.salutation ?? "",
                contactPersonFirstName: proposal?.contact_person?.firstname ?? "",
                contactPersonMiddleName: proposal?.contact_person?.mi ?? "",
                contactPersonLastName: proposal?.contact_person?.lastname ?? "",
                // contactNumber: proposal?.contact_number?.replace("+63", "") ?? "",
                contactNumber: proposal.contact_person_contact.contact_person_mobile?.replace("+63", "") ?? "",
                contactTelephone: proposal.contact_person_contact.contact_person_telephone ?? "",
                tinNumber: proposal.company_details.company_tin ?? '',
                secNumber: proposal.company_details.company_sec ?? '',
            }));

            const category: SubTypeOption = {
                name: proposal?.business_nature?.name ?? '',
                id: proposal?.business_nature?.id ?? 0,
            }
            const subItem: SubTypeOption = {
                name: proposal?.business_nature?.sub_nature?.name ?? '',
                id: proposal?.business_nature?.sub_nature?.id ?? 0
            }
            dispatch(setNatureOfBusiness({value: category, type: 'category'}))
            dispatch(setNatureOfBusiness({value: subItem, type: 'item'}))
            // PROPOSAL EXPIRED AT
            dispatch(setProposalExpiredAt(proposal.validity.expiry_date))
            
            dispatch(setCurrentGroupName(proposal?.group_name ?? "",))
            dispatch(setCurrentStatus(proposal?.status.name ?? "",))
    }, [dispatch, proposal]);
    // DONE
    const populateClassification = useCallback(() => {
        if (!proposal) return;

        dispatch(setGroupClassification({
            id: proposal.group_classification?.id,
            name: proposal.group_classification?.name
        }));
        dispatch(setOtherSpecification(proposal.group_classification?.other_value ?? null));
    }, [dispatch, proposal]);
    // NONE
    const populateBusinessType = useCallback(() => {
        if (!proposal) return;

        // dispatch(setBusinessType({
        //     id: proposal.business_type.id,
        //     name: proposal.business_type.name
        // }));
        // dispatch(setOtherSpecificationBT(proposal.business_type.other_value ?? null));
    }, [dispatch, proposal]);
    // DONE
    const populateTypeOfGroup = useCallback(() => {
        if (!proposal) return;

        dispatch(setTypeOfGroup({id: proposal.group_type?.id, name: proposal.group_type?.name}));
        dispatch(setSelectedSubTypesFromAPI(proposal.sub_group_types));
        dispatch(setOtherSpecificationToG(proposal.group_type?.other_value ?? null));
    }, [dispatch, proposal]);
    // DONE
    const populateAgeProfile = useCallback(() => {
        if (!proposal) return;
        dispatch(setAgeByLabel({
            label: "Minimum Age",
            age: proposal?.minimum_age ?? null,
        }));
        dispatch(setAgeByLabel({
            label: "Max Age",
            age: proposal?.maximum_age ?? null,
        }));
        const remainingAge = generateRemainingAgeRate(proposal?.minimum_age ?? 0, proposal?.maximum_age ?? 0)
        remainingAge.forEach(item => {
            dispatch(addBorrowerAgeOption({
                    id: item.id,
                    label: item.label,
                    typeOfAge: item.typeOfAge,
                    type: "rm",
                })
            );
        });
    }, [dispatch, proposal]);
    // DONE
    const populateFund = useCallback(() => {
        if (!proposal) return;

        dispatch(setSelectedFundingArrangementsBox({
            id: proposal?.payment_mode?.id,
            name: proposal?.payment_mode?.name
        }));
    }, [dispatch, proposal]);
    // DONE
    const populateProposalCategory = useCallback(() => {
        if (!proposal) return;

        dispatch(setSelectedProposalTypeBox(proposal?.type_of_proposal ?? null));
        dispatch(setSelectedPrototypeTypeBox(proposal?.prototype_plan ?? null));
        dispatch(setSelectedProductTypeBox(proposal?.plan ?? null));
        // dispatch(setPlansAndRidersByProposalType(proposal?.plan.id ?? null));
        dispatch(setSelectedBasicPlansBox(proposal?.basic_plan ?? null));

        if(proposal?.plan?.id === PRODUCT_TYPE_IDS.GCI) {
            dispatch(setBorrowerAgeIsShow({ id: 6, isShow: true }))
            dispatch(setBorrowerAgeIsShow({ id: 3, isShow: false }))
            dispatch(setBorrowerAgeIsShow({ id: 4, isShow: false }))
            dispatch(setBorrowerAgeIsShow({ id: 5, isShow: false }))
        } else {
            dispatch(setBorrowerAgeIsShow({ id: 6, isShow: false }))
            dispatch(setBorrowerAgeIsShow({ id: 3, isShow: true }))
            dispatch(setBorrowerAgeIsShow({ id: 4, isShow: true }))
            dispatch(setBorrowerAgeIsShow({ id: 5, isShow: true }))
        }
    }, [dispatch, proposal]);
    // DONE
    const populateAmountOfLoans = useCallback(() => {
        if (!proposal) return;
        if (proposal.plan?.id !== PRODUCT_TYPE_IDS.GCLI) return;

        dispatch(setSelectedAmountOfLoansBox({
            id: proposal.amount_loans?.id ?? 0,
            name: proposal.amount_loans?.name ?? ""
        }));
        dispatch(setSelectedAmountOfLoansInputFromAPI({
            label: proposal.amount_loans?.name ?? "",
            fAmount: Number(proposal.loans_amount ?? 0).toLocaleString("en-US")
        }));
        dispatch(updateAmountOfLoansRiderFAmount({
            riderId: 1,
            fAmount: Number(proposal.loans_amount ?? 0)
        }));
        dispatch(setFAmountOfLoans(proposal.loans_amount ?? 0));
        dispatch(setUpdateAmount({ type: 'min', amount: proposal.min_loan_amount ?? 0}))
        dispatch(setUpdateAmount({ type: 'max', amount: proposal.max_loan_amount ?? 0}))
        dispatch(setUpdateAmount({ type: 'port', amount: proposal.loan_portfolio_amount ?? 0}))
    }, [dispatch, proposal]);
    // DONE
    const populateTypeOfProduct = useCallback(() => {
        if(!proposal) return
        if(proposal.type_of_proposal?.id === PROPOSAL_TYPE_IDS.prototype) {
            dispatch(setIsPrototype(true))
            return
        }
        if(proposal.type_of_proposal?.id === PROPOSAL_TYPE_IDS.customized) {
            dispatch(setIsPrototype(false))
            return
        }
    }, [dispatch, proposal]);
    // DONE
    const populateCoverage  = useCallback(() => {
        if (!proposal) return;

        dispatch(setSelectedCoverageTypeBox({
            id: proposal.coverage_type?.id ?? 0,
            name: proposal.coverage_type?.name ?? ""
        }));
    }, [dispatch, proposal]);
    // DONE
    // populateGYRTorGPAorGCI
    const populateGYRTorGPA  = useCallback(() => {
        if (!proposal) return;
        const planId = proposal.plan?.id;
        if (planId !== PRODUCT_TYPE_IDS.GYRT && planId !== PRODUCT_TYPE_IDS.GPA && planId !== PRODUCT_TYPE_IDS.GCI) return;

        const mappedLevelRanking = proposal?.level_ranking?.map(item => ({
            id: uuidv4(),
            label: item.designation,
            fAmount: item.amount,
        })) ?? [];
        // LEVEL RANKING
        dispatch(setLevelRankingFromAPI(mappedLevelRanking ?? []));
        // UNIFORM COVERAGE
        dispatch(setSelectedUniformCoverage({
            label: proposal?.coverage_type?.name ?? "",
            value: proposal?.uniform_coverage_amount ?? 0,
        }));

        // SALARY RANKING
        dispatch(setSalaryRankFromAPI(proposal.salary_ranking ?? []));
    }, [dispatch, proposal]);
    // Missing
    const populateRidersPlan = useCallback(() => {
        if (!proposal) return;
        const planId = proposal.plan?.id;
        const riders = proposal.riders ?? [];
        const coverageName = proposal.coverage_type?.name;
        const isLevelRanking = proposal.coverage_type?.id === COVERAGE_TYPE_IDS.levelRanking;
        const isUniformCoverage = proposal.coverage_type?.id === COVERAGE_TYPE_IDS.uniformCoverage;
        const isSalaryRank = proposal.coverage_type?.id === COVERAGE_TYPE_IDS.salaryRank;
        
        switch (planId) {
            case PRODUCT_TYPE_IDS.GYRT:
            case PRODUCT_TYPE_IDS.GPA:
                if (isLevelRanking) {
                    populateRidersLevelRanking(dispatch, riders, 'current', optionForGroupDengueRider);
                } else if (isUniformCoverage) {
                    populateRidersUniformCoverage(dispatch, coverageName, riders, 'current', optionForGroupDengueRider);
                } else if (isSalaryRank) {
                    populateRidersSalaryRank(dispatch, riders, 'current', optionForGroupDengueRider);
                }
                break;

            case PRODUCT_TYPE_IDS.GCLI:
                populateRidersAmountOfLoans(dispatch, coverageName, riders, 'current', optionForGroupDengueRider);
                break;

            default:
                break;
        }
    }, [dispatch, proposal]);
    // DONE
    const populateRiders = useCallback(() => {
        if (!proposal) return;
        const formatted: RiderCategoryType[] = proposal?.riders?.map((r: Riders) => ({
            id: r.rider_id,
            label: r.rider_name,
        })) ?? [];
        dispatch(setSelectedRiders(formatted));
    }, [dispatch, proposal]);
    // DONE
    const populatePayments = useCallback(() => {
        if (!proposal) return;
        if (proposal.plan?.id !== PRODUCT_TYPE_IDS.GCLI) return;

        dispatch(setSelectedPaymentTermBox({
            id: proposal?.payment?.[0]?.payment_term.id ?? 0,
            name: proposal?.payment?.[0]?.payment_term.name ?? ""
        }))
        // LOAN MATURITY
        // dispatch(setSelectedLoanMaturityBox({
        //     id: proposal?.payment?.[0]?.sub_payment_term.id ?? 0,
        //     name: proposal?.payment?.[0]?.sub_payment_term.name ?? ""
        // }))
        dispatch(setUpdateLoanMaturityMonths(Number(proposal?.payment?.[0]?.sub_payment_term.name)))

    }, [dispatch, proposal]);
    // DONE
    const populateBorrowAge = useCallback(() => {
        if (!proposal) return;

        dispatch(setBorrowerAgeFromAPI({
            type: 'check',
            value: {
                borrower_age_66_70: proposal?.borrower_age_66_70 ?? false,
                borrower_age_71_75: proposal?.borrower_age_71_75 ?? false,
                borrower_age_76_80: proposal?.borrower_age_76_80 ?? false,
            }
        }));
        dispatch(setBorrowerAgeFromAPI({
            type: 'amount',
            fAmount: {
                borrower_amount_66_70: proposal?.borrower_amount_66_70 ?? null,
                borrower_amount_71_75: proposal?.borrower_amount_71_75 ?? null,
                borrower_amount_76_80: proposal?.borrower_amount_76_80 ?? null,
            }
        }));
    }, [dispatch, proposal]);
    // DONE
    const populateNotes = useCallback(() => {
        if (!proposal) return;

        dispatch(setNotesContent(proposal.notes ?? ''))
    }, [dispatch, proposal]);
    // DONE
    const populateCSVFile = useCallback(() => {
        if (!proposal) return;
        dispatch(setExistingCsvFilePath(proposal.excel_file_path ?? ''))
    }, [dispatch, proposal]);
    // DONE
    const populateBorrowerAge1865ForComputation = useCallback(() => {
        if(!proposal) return;
        // borrower_under_max: boolean
        // borrower_under_min: boolean
        const minAge = proposal?.minimum_age ?? null;
        const maxAge = proposal?.maximum_age ?? null;
        if(!minAge || !maxAge) return
        // const remainingAge = generateRemainingAgeRate(minAge, maxAge)
        // remainingAge.forEach(item => {
        //     let amount = 0;
        //     switch (item.typeOfAge) {
        //         case "under_min":
        //             amount = proposal.borrower_amount_under_min ?? 0;
        //             break;
        //         case "main":
        //             amount = proposal.borrower_amount_18_65 ?? 0;
        //             break;
        //         case "under_max":
        //             amount = proposal.borrower_amount_under_max ?? 0;
        //             break;
        //     }

        //     dispatch(
        //         updateBorrowerAgeAmount({
        //             id: item.id,
        //             amount,
        //         })
        //     );
        // });
        
        const under_min = proposal.borrower_under_min ?? false
        const under_max = proposal.borrower_under_max ?? false

        const default1865Value = proposal.borrower_amount_18_65 ?? 0
        const under_min_value = proposal.borrower_amount_under_min ?? 0
        const under_max_value = proposal.borrower_amount_over_max ?? 0

        dispatch(updateBorrowerAgeValue({ id: 1, value: under_min, amount: 0 }))
        dispatch(updateBorrowerAgeValue({ id: 2, value: true, amount: default1865Value }))
        dispatch(updateBorrowerAgeValue({ id: 3, value: under_max, amount: under_max_value }))
    }, [dispatch, proposal]);

    const populateProposalRemainingAge = useCallback(() => {
        if(!proposal) return;
        // const under_min = proposal.borrower_under_max ?? false
        // const under_max = proposal.borrower_under_min ?? false

        // dispatch(updateBorrowerAgeValue({ id: 1, value: under_min }))
        // dispatch(updateBorrowerAgeValue({ id: 3, value: under_max }))
    }, [dispatch, proposal]);

    const populateProposalPreparedBy = useCallback(() => {
        if(!proposal) return;
        dispatch(setProposalPreparedBy({
            id: proposal?.user_id,
            name: proposal?.user_full_name,
        }))
    }, [dispatch, proposal]);
    
    const populateProposalStatus = useCallback(() => {
        if(!proposal) return;
        dispatch(setProposalStatus(proposal?.status))
        dispatch(setUpdateStatus({ type: 'ebam', value: proposal?.ebam_status}))
        dispatch(setUpdateStatus({ type: 'actuarial', value: proposal?.actuarial_status}))
    }, [dispatch, proposal]);

    const populateProposalSupportingDocuments = useCallback(() => {
        if(!proposal) return;
        dispatch(populateSupportingDocuments(
            proposal.supporting_details?.map(item => ({
                fileName: item.file_name,
                filePath: item.file_path,
            })) ?? []
        ))
    }, [dispatch, proposal]);

    const populatePolicy = useCallback(() => {
        if(!proposal) return;
        dispatch(setPolicyNumber(proposal.policy_no ?? ''))
    }, [dispatch, proposal]);

    return { 
        populateChannelType,
        populateCommissionRateAndServiceFee,
        populateAffiliates,
        populateBasicInfo,
        populateClassification,
        populateBusinessType,
        populateTypeOfGroup,
        populateAgeProfile,
        populateFund,
        populateProposalCategory,
        populateAmountOfLoans,
        populateCoverage,
        populateGYRTorGPA,
        populateRidersPlan,
        populatePayments,
        populateBorrowAge,
        populateNotes,
        populateRiders,
        populateTypeOfProduct,
        populateCSVFile,
        
        populateBorrowerAge1865ForComputation,
        populateProposalRemainingAge,
        populateProposalPreparedBy,
        populateProposalStatus,

        populateProposalSupportingDocuments,
        populateApproval,

        populatePolicy,
    };
}